import { inject, Injectable } from "@angular/core";
import { DateUtils } from "@arch/_shared/utils";
import { EventsService } from "@arch/domain/application/services/events.service";
import { ReportPreferenceService } from "@arch/domain/application/services/report-preference.service";
import { ReportEntity } from "@arch/domain/entity/report.entity";
import { VisualizationEntity } from "@arch/domain/entity/visualization.entity";
import { environment } from "@environments/environment";
import { DatePeriodService } from "./date-period.service";

@Injectable({
    providedIn: "root"
})
export class PdfService {
    private readonly datePeriodService = inject(DatePeriodService);
    private readonly eventsService = inject(EventsService);
    private readonly reportPreferenceService = inject(ReportPreferenceService);
    private report: ReportEntity;
    public tableValuesDates: Date[][] = [];
    public indexPages: {
        visualizations: {
            _id: string;
            name: string;
            type: string;
            sourceLabel?: string;
        }[];
    }[] = [];
    public visualizationsTableValuesPages: Record<string, Date[][][]> = {};
    public linkDigital: string = "";
    public config(report: ReportEntity): void {
        this.report = report;
        document.title = this.report.props.name;
        this.setLinkDigital();
        this.setIndexPages();
        this.setTableValuesDates();
        this.setVisualizationsTableValuesPages();
        this.eventsService.send(
            `Dash.${this.report.type}.${this.report.humanSourceName}.preview-pdf`
        );
    }

    private setLinkDigital(): void {
        const type = this.report.type;
        const types: Record<string, string> = {
            dashboard: "dashboard",
            dre: "dre",
            dreComparation: "dre",
            balance: "balanco",
            balanceComparation: "balanco",
            budget: "orcamento",
            projection: "projecao"
        };
        this.linkDigital = `${environment.BASEURL}/${types[type]}?companyId=${this.report.props.companyId}&reportId=${this.report.props._id}&liveToken=${environment.LIVE_TOKEN}`;
    }

    private setTableValuesDates(): void {
        let dates = this.datePeriodService.getDates(this.report.options);
        if (this.report.options.comparation.allMonths) {
            dates = dates.sort((a, b) => a.getMonth() - b.getMonth());
        }

        this.tableValuesDates = DateUtils.chunkDates(
            dates,
            this.report?.options?.budget || this.report?.options?.projection
                ? 3
                : 6
        );
    }

    private setIndexPages(): void {
        const hasDre = ["dre", "dreComparation"].includes(this.report.type);
        if (!this.report?.visualizations?.visualzations) {
            this.indexPages = [];
            return;
        }
        const visualizations: {
            _id: string;
            name: string;
            type: string;
            sourceLabel?: string;
        }[] = this.report.visualizations.visualzations.map((visualization) => ({
            _id: visualization.props._id,
            name: visualization.props.name,
            type:
                visualization.props.chart?.category ||
                visualization.props.component?.name ||
                "column",
            sourceLabel: visualization.sourceLabel
        }));
        const firstPageIndex =
            !this.reportPreferenceService.viewBench &&
            !this.reportPreferenceService.viewPeformance;
        const maxItems = {
            first: firstPageIndex ? 26 : 32,
            second: firstPageIndex ? 28 : 33
        };
        const pageSize =
            hasDre && this.report.hasBalance
                ? maxItems.first
                : hasDre
                  ? maxItems.second
                  : maxItems.first;
        const pages: { visualizations: typeof visualizations }[] = [];
        for (let i = 0; i < visualizations.length; i += pageSize) {
            pages.push({
                visualizations: visualizations.slice(i, i + pageSize)
            });
        }
        this.indexPages = pages;
        if (hasDre) {
            this.indexPages[0].visualizations.unshift({
                _id: "TableDre",
                name: "DRE",
                type: "table_dre"
            });
        }
        if (this.report.hasBalance) {
            this.indexPages[0].visualizations.unshift(
                {
                    _id: "TableBalance",
                    name: "Balanço Patrimonial Ativo",
                    type: "table_dre"
                },
                {
                    _id: "TableBalance",
                    name: "Balanço Patrimonial Passivo",
                    type: "table_dre"
                }
            );
        }
    }

    private setVisualizationsTableValuesPages(): void {
        if (!this.report.visualizations?.visualzations) return;
        this.report.visualizations.visualzations.forEach(
            (visualization, index) => {
                const tablesPerPage = this.breakTableValuesPage(
                    visualization,
                    index
                );
                if (tablesPerPage) {
                    const tableValuesDatesChunks = this.chunkTableValuesDates(
                        this.tableValuesDates,
                        tablesPerPage
                    );
                    this.visualizationsTableValuesPages[
                        visualization.props._id
                    ] = tableValuesDatesChunks;
                } else {
                    this.visualizationsTableValuesPages[
                        visualization.props._id
                    ] = [this.tableValuesDates];
                }
            }
        );
    }

    private chunkTableValuesDates(
        tableValuesDates: Date[][],
        chunkSize: number
    ): Date[][][] {
        if (!tableValuesDates || !Array.isArray(tableValuesDates)) return [];
        const dateChunks: Date[][][] = [];
        for (let i = 0; i < tableValuesDates.length; i += chunkSize) {
            dateChunks.push(tableValuesDates.slice(i, i + chunkSize));
        }
        return dateChunks;
    }

    public breakTableValuesPage(
        visualization: VisualizationEntity,
        index: number
    ): number {
        if (this.tableValuesDates.length >= 9) {
            if (visualization.props.series?.length === 1)
                return index === 0 ? 9 : 10;
        }
        if (this.tableValuesDates.length >= 6) {
            if (visualization.props.series?.length >= 4) return 4;
            if (visualization.props.series?.length === 3) return 5;
            if (visualization.props.series?.length === 2) return 7;
        }
        if (
            this.tableValuesDates.length >= 4 &&
            visualization.props.series?.length >= 4
        )
            return 4;

        if (visualization.props.series?.length >= 11) return 1;

        return 0;
    }

    public print(timeout: number = 3500): void {
        setTimeout(() => {
            window.print();
        }, timeout);
    }

    public printAgain(): void {
        window.print();
        this.eventsService.send(
            `Dash.${this.report.type}.${this.report.humanSourceName}.baixar-pdf`
        );
    }
}
